import SentryService from '@services/SentryService';

import type { DeviceEnumerator } from './DeviceEnumerator';
import {
  DEFAULT_MIC_ACQUIRE_TIMEOUT_MS,
  getUserMediaWithTimeout,
  MicrophoneAcquireTimeoutError,
} from './getUserMediaWithTimeout';
import type { AcquiredStream, AudioConstraints, FallbackReason, NormalizedDevice } from './types';

/** Default audio processing flags optimized for transcription fidelity */
const DEFAULT_CONSTRAINTS: AudioConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/**
 * DOMException names that mean "this device can't be used right now", as
 * opposed to NotAllowedError, where the user refused and no device will do.
 */
const FALLBACK_REASON_BY_ERROR_NAME: Record<string, FallbackReason> = {
  OverconstrainedError: 'overconstrained',
  NotFoundError: 'not_found',
  /** Held by another app or tab, e.g. a telehealth site owning the same earbuds. */
  NotReadableError: 'not_readable',
  /** Firefox reports a busy device as "AbortError: Starting audio failed". */
  AbortError: 'not_readable',
};

/** Why an exact-device attempt should be abandoned for the system default, if at all. */
export function fallbackReasonFor(error: unknown): FallbackReason | undefined {
  if (error instanceof MicrophoneAcquireTimeoutError) return 'timeout';
  if (error instanceof DOMException) return FALLBACK_REASON_BY_ERROR_NAME[error.name];
  return undefined;
}

export interface StreamAcquirerOptions {
  /** Wall-clock cap per getUserMedia attempt. */
  acquireTimeoutMs?: number;
}

/**
 * Acquires a MediaStream with exact→fallback strategy.
 * If the requested device is unavailable — missing, held by another app or
 * tab, or never handed over by the OS — falls back to the system default
 * and reports the substitution.
 */
export class StreamAcquirer {
  private readonly acquireTimeoutMs: number;

  constructor(
    private enumerator: DeviceEnumerator,
    options: StreamAcquirerOptions = {},
  ) {
    this.acquireTimeoutMs = options.acquireTimeoutMs ?? DEFAULT_MIC_ACQUIRE_TIMEOUT_MS;
  }

  /**
   * Acquire a media stream for the given device.
   *
   * Strategy:
   * 1. Try exact deviceId constraint, bounded by the acquire timeout
   * 2. If the device is unavailable (see `fallbackReasonFor`), retry without constraint
   * 3. Verify the actual device via track.getSettings()
   * 4. Report substitution if the acquired device differs
   */
  async acquire(browserDeviceId?: string, options?: AudioConstraints): Promise<AcquiredStream> {
    const constraints = { ...DEFAULT_CONSTRAINTS, ...options };
    let fallbackReason: FallbackReason | undefined;

    // Attempt 1: exact device constraint
    if (browserDeviceId) {
      SentryService.addBreadcrumb({
        category: 'audio.stream',
        message: 'Attempting exact device acquisition',
        data: { deviceId: browserDeviceId },
        level: 'info',
      });

      try {
        const stream = await this.getUserMedia(
          { deviceId: { exact: browserDeviceId }, ...constraints },
          'exact',
          browserDeviceId,
        );
        return this.buildResult(stream, browserDeviceId);
      } catch (error: unknown) {
        fallbackReason = fallbackReasonFor(error);
        if (!fallbackReason) throw error;

        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: `Exact acquisition failed (${fallbackReason}), falling back to default`,
          data: { requestedDeviceId: browserDeviceId },
          level: 'warning',
        });
      }
    }

    // Attempt 2: no device constraint (system default)
    SentryService.addBreadcrumb({
      category: 'audio.stream',
      message: 'Acquiring stream with system default device',
      level: 'info',
    });

    const stream = await this.getUserMedia(constraints, 'default', browserDeviceId);
    return this.buildResult(stream, browserDeviceId, fallbackReason);
  }

  /**
   * One bounded getUserMedia call, tagged with the device it was reaching
   * for. A hung exact attempt is a warning because the default attempt may
   * still recover the session; a hung default attempt is the end of the road.
   */
  private getUserMedia(
    audio: MediaTrackConstraints,
    attempt: 'exact' | 'default',
    requestedDeviceId?: string,
  ): Promise<MediaStream> {
    return getUserMediaWithTimeout(
      { audio, video: false },
      {
        attempt,
        severity: attempt === 'exact' ? 'warning' : 'error',
        timeoutMs: this.acquireTimeoutMs,
        requestedDeviceId,
        requestedTransport: requestedDeviceId
          ? this.enumerator.findByBrowserDeviceId(requestedDeviceId)?.transport
          : undefined,
      },
    );
  }

  /**
   * Build an AcquiredStream result, detecting substitution by comparing
   * the requested deviceId with the actual device from track.getSettings().
   */
  private buildResult(
    stream: MediaStream,
    requestedDeviceId?: string,
    fallbackReason?: FallbackReason,
  ): AcquiredStream {
    const track = stream.getAudioTracks()[0];
    const settings = track?.getSettings();
    const actualDeviceId = settings?.deviceId;

    // Try to find the actual device in our normalized list
    let device: NormalizedDevice | undefined;
    if (actualDeviceId) {
      device = this.enumerator.findByBrowserDeviceId(actualDeviceId);
    }

    // If we can't find the device in our list, create a minimal representation
    if (!device) {
      device = {
        internalId: actualDeviceId || 'unknown',
        browserDeviceId: actualDeviceId || '',
        displayLabel: track?.label || 'Unknown Microphone',
        rawLabel: track?.label || '',
        isDefault: false,
        isCommunications: false,
        transport: 'unknown',
      };
    }

    const wasSubstituted = !!(
      requestedDeviceId &&
      actualDeviceId &&
      requestedDeviceId !== actualDeviceId
    );

    if (wasSubstituted) {
      SentryService.addBreadcrumb({
        category: 'audio.stream',
        message: 'Device substitution detected',
        data: {
          requested: requestedDeviceId,
          actual: actualDeviceId,
          actualLabel: device.displayLabel,
          fallbackReason,
        },
        level: 'warning',
      });
    }

    SentryService.addBreadcrumb({
      category: 'audio.stream',
      message: 'Stream acquired successfully',
      data: {
        deviceId: actualDeviceId,
        label: device.displayLabel,
        wasSubstituted,
        fallbackReason,
        settings: {
          echoCancellation: settings?.echoCancellation,
          noiseSuppression: settings?.noiseSuppression,
          autoGainControl: settings?.autoGainControl,
        },
      },
      level: 'info',
    });

    return {
      stream,
      device,
      wasSubstituted,
      requestedDeviceId,
      fallbackReason,
    };
  }
}
